import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper';
import { createApp, onMounted, onUnmounted, ref } from 'vue';

import { useViewportSize } from '~/composables/use-viewport-size';
import { ANIMATION_CONFIGS, ANIMATION_DURATIONS, BORDER_RADIUS } from '~/constants/animations';

import * as AnalyticsData from '../analytics/analytics-data';
import { BaseSection } from './base-section';

gsap.registerPlugin(ScrollTrigger);

/**
 * @type {import('swiper/types').SwiperOptions}
 */
const CAROUSEL_OPTIONS = {
  breakpoints: {
    768: {
      enabled: false,
      slidesOffsetAfter: 0,
      slidesOffsetBefore: 0,
      spaceBetween: 0,
    },
  },
  centeredSlides: true,
  centeredSlidesBounds: true,
  slidesPerView: 1.4,
  spaceBetween: 10,
};

const HOVER_DELAY = ANIMATION_DURATIONS.ELEMENT.HOVER.DELAY;
const SECTION_NAME = 'category-row';

class CategoryRow extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;
    const sectionInstance = this;

    this.setupInViewAnimation(instanceData);

    createApp({
      name: 'CategoryRowSection',
      setup() {
        const swiperInstance = ref(null);
        const activeItem = ref(null);
        const categoryItems = ref([]);
        const animations = ref([]);
        const hoverTimeouts = {};
        const isScrollAnimationComplete = ref(false);
        const { isDesktop, isSmall, isTouch } = useViewportSize();

        let gsapCtx = null;

        const setupCarousel = () => {
          const swiperEl = container.querySelector('.swiper');

          if (!swiperEl) return;

          swiperInstance.value = new Swiper(swiperEl, {
            ...CAROUSEL_OPTIONS,
            on: {
              slideChange: ({ activeIndex }) => {
                playActiveItemAnimation(activeIndex);
              },
            },
          });
        };

        const playActiveItemAnimation = (index) => {
          if (activeItem.value !== null && activeItem.value !== index) {
            animations.value[activeItem.value]?.reverse();
          }

          animations.value[index]?.play();
          activeItem.value = index;
        };

        const setupAnimations = () => {
          categoryItems.value.forEach((item, i) => {
            if (!item) return;

            const hoverImage = item.querySelector('.category-row-item-hover-image');
            const title = item.querySelector('.category-row-item-title');
            const ctaButtons = item.querySelector('.category-row-item-cta-buttons');
            const textContent = item.querySelector('[data-text-content]');
            if (!hoverImage || !title || !ctaButtons || !textContent) return;

            // Set initial states
            gsap.set(hoverImage, { opacity: 0 });
            gsap.set(ctaButtons, { opacity: 0 });
            gsap.set(textContent, { yPercent: 30 });
            gsap.set(title, { opacity: 0 });
            gsap.set(item, { borderRadius: BORDER_RADIUS.DEFAULT });

            // Create a single reversible timeline for hover/unhover
            const tl = gsap.timeline({
              defaults: { overwrite: 'auto' },
              onComplete: () => {
                ctaButtons.querySelectorAll('a').forEach((a) => {
                  a.style.pointerEvents = 'auto';
                });
              },
              onReverseComplete: () => {
                ctaButtons.querySelectorAll('a').forEach((a) => (a.style.pointerEvents = 'none'));
              },
              paused: true,
            });

            tl.to(item, ANIMATION_CONFIGS.BORDER_RADIUS_PILL, 0)
              .to(hoverImage, ANIMATION_CONFIGS.FADE_IN_IMAGE, 0)
              .to(title, ANIMATION_CONFIGS.FADE_OUT_BORDER_OPACITY, 0)
              .to(textContent, ANIMATION_CONFIGS.MOVE_UP_IN_OUT, 0)
              .to(ctaButtons, ANIMATION_CONFIGS.FADE_IN_CTA, 0);

            // Store the timeline
            animations.value[i] = tl;
          });

          const titles = container.querySelectorAll('.category-row-item-title');

          if (!titles.length) return;

          gsap.to(
            titles,
            {
              ...ANIMATION_CONFIGS.STAGGERED_FADE_IN,
              onComplete: () => {
                isScrollAnimationComplete.value = true;
              },
              scrollTrigger: {
                start: 'top 75%',
                trigger: container,
              },
            },
            0,
          );
        };

        // On mouse enter, delay the hover animation to create a hover intent
        const handleMouseEnter = (index) => {
          if (activeItem.value !== null && activeItem.value !== index) {
            animations.value[activeItem.value]?.reverse();
          }
          // Set a delay before playing the hover animation
          hoverTimeouts[index] = setTimeout(() => {
            const tl = animations.value[index];
            if (tl) {
              tl.play();
              activeItem.value = index;
            }
          }, HOVER_DELAY * 1000);
        };

        const handleClick = (index) => {
          // Click is only for tablets
          if (!isTouch.value || isDesktop.value || isSmall.value) return;

          const tl = animations.value[index];
          if (!tl) return;

          if (activeItem.value === index) return;

          playActiveItemAnimation(index);
        };

        // On mouse leave, clear the timeout if it hasn't fired, or reverse the animation
        const handleMouseLeave = (index) => {
          // Skip if scroll animation isn't complete
          if (!isScrollAnimationComplete.value) return;

          if (hoverTimeouts[index]) {
            clearTimeout(hoverTimeouts[index]);
            hoverTimeouts[index] = null;
          }
          const tl = animations.value[index];
          if (tl) {
            tl.reverse();
            if (activeItem.value === index) {
              activeItem.value = null;
            }
          }
        };

        onMounted(() => {
          setupCarousel();
          gsapCtx = gsap.context(() => {
            // Collect all category row items
            categoryItems.value = Array.from(container.querySelectorAll('.category-row-item'));
            setupAnimations();
          }, container);

          if (!isDesktop.value) {
            playActiveItemAnimation(0);
          }

          ScrollTrigger.refresh();

          // Hook analytics event to CTA links
          if (sectionInstance.isHomePageTemplate()) {
            AnalyticsData.hookHomepageModuleClicked({
              sectionName: SECTION_NAME,
            });
          }
        });

        onUnmounted(() => {
          if (swiperInstance.value) {
            swiperInstance.value.destroy();
          }
          gsapCtx?.revert();
          // Clear any pending hover timeouts
          Object.values(hoverTimeouts).forEach((timeout) => clearTimeout(timeout));
        });

        return {
          activeItem,
          categoryItems,
          handleClick,
          handleMouseEnter,
          handleMouseLeave,
        };
      },
    }).mount(container);
  }

  onUnload(instanceData) {
    const app = instanceData.container.__vue_app__;

    // Kill any ScrollTrigger instances
    if (instanceData.animation && instanceData.animation.scrollTrigger) {
      instanceData.animation.scrollTrigger.kill();
    }

    // Kill the animation
    if (instanceData.animation) {
      instanceData.animation.kill();
      instanceData.animation = null;
    }

    if (app) {
      app.unmount();
    }
  }

  /**
   * Sets up a ScrollTrigger animation
   * This is used to initialize the animation with ScrollTrigger instead of the default behavior
   *
   * @param {Object} instanceData - The section instance data
   */
  setupInViewAnimation(instanceData) {
    if (instanceData.animation) {
      instanceData.animation.paused(false);

      ScrollTrigger.refresh();
    } else {
      const animation = this.setupAnimation(instanceData, () =>
        this.setupInViewAnimationTimeline(instanceData),
      );

      if (animation && animation.scrollTrigger) {
        instanceData.scrollTrigger = animation.scrollTrigger;
      }
    }
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    if (!container) return null;

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        start: 'top 100%',
        trigger: container,
      },
    });

    if (!tl) return null;
    tl.to(container, ANIMATION_CONFIGS.FADE_IN, ANIMATION_DURATIONS.SECTION.FADE_IN);

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CategoryRow();
});
